import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  LinearProgress,
  Chip,
  Stack,
} from '@mui/material';
import {
  CloudUpload,
  AudioFile as AudioFileIcon,
  Close,
  Info,
} from '@mui/icons-material';
import { AudioFile } from '../types';
import AudioPlayer from './AudioPlayer';

interface FileUploadProps {
  title: string;
  subtitle?: string;
  file: AudioFile | null;
  onFileSelect: (file: AudioFile) => void;
  onFileRemove: () => void;
  accept?: Record<string, string[]>;
  maxSize?: number;
  disabled?: boolean;
  uploadProgress?: number;
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDuration = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const FileUpload: React.FC<FileUploadProps> = ({
  title,
  subtitle,
  file,
  onFileSelect,
  onFileRemove,
  accept = {
    'audio/*': ['.wav', '.mp3', '.flac', '.aiff', '.aac', '.ogg', '.m4a'],
  },
  maxSize = 150 * 1024 * 1024, // 150MB
  disabled = false,
  uploadProgress,
}) => {
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        const selectedFile = acceptedFiles[0];

        // Create audio element to get duration and other metadata
        const audio = new Audio();
        audio.preload = 'metadata';

        audio.onloadedmetadata = () => {
          const audioFile: AudioFile = {
            file: selectedFile,
            name: selectedFile.name,
            size: selectedFile.size,
            duration: audio.duration,
            url: URL.createObjectURL(selectedFile),
          };

          onFileSelect(audioFile);
        };

        audio.onerror = () => {
          // If we can't load metadata, still create the file object
          const audioFile: AudioFile = {
            file: selectedFile,
            name: selectedFile.name,
            size: selectedFile.size,
            url: URL.createObjectURL(selectedFile),
          };

          onFileSelect(audioFile);
        };

        audio.src = URL.createObjectURL(selectedFile);
      }
    },
    [onFileSelect]
  );

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept,
    maxSize,
    multiple: false,
    disabled,
  });

  const hasFile = file !== null;
  const isUploading = uploadProgress !== undefined && uploadProgress < 100;

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
          {subtitle}
        </Typography>
      )}

      {!hasFile ? (
        <Paper
          {...getRootProps()}
          elevation={isDragActive ? 8 : 2}
          sx={{
            p: 1.5,
            textAlign: 'center',
            cursor: disabled ? 'not-allowed' : 'pointer',
            border: '2px dashed',
            borderColor: isDragActive ? 'primary.main' : 'grey.300',
            backgroundColor: isDragActive ? 'action.hover' : 'background.paper',
            transition: 'all 0.2s ease-in-out',
            opacity: disabled ? 0.6 : 1,
            '&:hover': {
              borderColor: disabled ? 'grey.300' : 'primary.main',
              backgroundColor: disabled ? 'background.paper' : 'action.hover',
            },
          }}
        >
          <input {...getInputProps()} />
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
            <CloudUpload sx={{ fontSize: 32, color: 'text.secondary' }} />
            <Typography variant="body2">
              {isDragActive ? 'Drop here' : 'Drop file or click'}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              • WAV, MP3, FLAC, AIFF, AAC, OGG, M4A • Max: {formatFileSize(maxSize)}
            </Typography>
          </Stack>
        </Paper>
      ) : (
        <Paper elevation={2} sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" spacing={2}>
            <AudioFileIcon color="primary" />
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" noWrap>
                {file.name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                <Chip label={formatFileSize(file.size)} size="small" variant="outlined" />
                {file.duration && (
                  <Chip
                    label={formatDuration(file.duration)}
                    size="small"
                    variant="outlined"
                    icon={<Info />}
                  />
                )}
              </Stack>
              {isUploading && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    Uploading: {uploadProgress}%
                  </Typography>
                </Box>
              )}
            </Box>
            {!isUploading && (
              <IconButton
                onClick={onFileRemove}
                color="error"
                size="small"
                disabled={disabled}
              >
                <Close />
              </IconButton>
            )}
          </Stack>

          {/* Audio Player */}
          {file.url && !isUploading && (
            <Box sx={{ mt: 2 }}>
              <AudioPlayer
                audioUrl={file.url}
                title="Preview"
                disabled={disabled}
              />
            </Box>
          )}
        </Paper>
      )}

      {fileRejections.length > 0 && (
        <Box sx={{ mt: 1 }}>
          {fileRejections.map(({ file, errors }) => (
            <Typography key={file.name} variant="body2" color="error">
              {file.name}: {errors.map(error => error.message).join(', ')}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FileUpload;